'use client';

import classnames from 'classnames';
import { AblyComponent } from '@stadionhq-v3/ably';
import {
  commentaryAblyMutator,
  LivelyCommentaryMessageResponse,
} from '@stadionhq-v3/transforms';
import {
  CommentaryEventIcons,
  CommentaryFeed,
  CommentaryIcons,
  CommentaryProps,
} from '@stadionhq-v3/football-ui';
import {
  CardRed,
  CardSecondYellow,
  CardYellow,
  Goal,
  OwnGoal,
  Substitution,
  Whistle,
} from '@components/icons';
import dayjs from '@lib/dayjs';
import { DEFAULT_LOCALE } from '@core/constants/locales.constants';
import styles from './matchCentreCommentary.module.scss';
import { MatchCentreCommentaryProps } from './matchCentreCommentary.types';
import { QA_MATCH_CENTRE_COMMENTARY } from '../../../testConfig/testIds';

const icons: CommentaryEventIcons = {
  [CommentaryIcons.Goal]: <Goal />,
  [CommentaryIcons.OwnGoal]: <OwnGoal />,
  [CommentaryIcons.RedCard]: <CardRed />,
  [CommentaryIcons.YellowCard]: <CardYellow />,
  [CommentaryIcons.Sub]: <Substitution />,
  [CommentaryIcons.SecondYellow]: <CardSecondYellow />,
  [CommentaryIcons.MatchEnds]: <Whistle />,
};

// The football-ui CommentTime formats each comment with a plain
// dayjs(dateAdded).format('HH:mm') (no .tz()), so a UTC timestamp that arrives
// without a `Z`/offset designator is parsed as naive local time and the comment
// time is shown as UTC. Normalise every dateAdded to a UTC ISO string (with `Z`)
// so the downstream formatting converts it to the user's local timezone.
// Idempotent for `Z`/offset inputs. Applied here (rather than in the Ably
// mutator) because the initial endpoint state bypasses the mutator, whereas the
// render callback receives the merged initial + live store.
const toUserLocalCommentary = (props: CommentaryProps): CommentaryProps => ({
  ...props,
  comments: props.comments
    ? props.comments.map((comment) => ({
        ...comment,
        dateAdded: comment.dateAdded
          ? dayjs(DEFAULT_LOCALE).utc(comment.dateAdded).toISOString()
          : comment.dateAdded,
      }))
    : props.comments,
});

const MatchCentreCommentary = ({
  /* props */
  dictionary,
  dataProviderId,
  livelyEventId,
  SPORTS_DATA_API,
  testId = QA_MATCH_CENTRE_COMMENTARY.COMPONENT,
  classNames = [],
  nativeProps = {},
  ...ablyProps
}: MatchCentreCommentaryProps) => {
  const classes = classnames([styles.matchCentreCommentary], classNames);

  return ablyProps ? (
    <AblyComponent<CommentaryProps, LivelyCommentaryMessageResponse>
      {...ablyProps}
      storeKey="Commentary"
      mutator={commentaryAblyMutator}
      channelNameKey="ablyChannel"
      messageNameKey="ablyMessageName"
      backfillOnReconnect
      render={(props: CommentaryProps) => (
        <CommentaryFeed
          {...toUserLocalCommentary(props)}
          {...nativeProps}
          icons={icons}
          classNames={[classes]}
          testId={testId}
          dictionary={dictionary}
          dataProviderId={dataProviderId}
          livelyEventId={livelyEventId}
          SPORTS_DATA_API={SPORTS_DATA_API}
        />
      )}
    />
  ) : (
    <></>
  );
};

export default MatchCentreCommentary;
